import { ResendReceiptButton } from "@/components/finance/ResendReceiptButton";
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SendInvoiceReminderDialog } from "@/components/finance/SendInvoiceReminderDialog";
import {
  Package,
  FileText,
  CheckCircle,
  Clock,
  ExternalLink,
  Copy,
  RefreshCw,
  MessageCircle,
  AlertCircle,
  Mail,
  Shield,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { copyTextToClipboard } from "@/lib/copyTextToClipboard";
import {
  shouldShowMarkAsPaidButton,
  shouldShowSendToXeroButton,
  shouldShowSentToXeroStatus,

} from "@shared/markPaidXeroWorkflow";
import { MarkPaidReceiptModeDialog } from "@/components/finance/MarkPaidReceiptModeDialog";
import { XeroPaymentSentStatus } from "@/components/finance/XeroPaymentSentStatus";
import {
  defaultPptPaymentTrancheLabel,
  resolvePptPaymentTrancheLabelForAdd,
  shouldAutoFillPptPaymentTrancheLabel,
} from "@shared/pptPaymentTrancheLabels";
import { canViewWarranty as canViewWarrantyRole } from "@shared/warrantyDomain";
import { normalizeUserRole } from "@shared/userRoles";
import { useLocation } from "wouter";

interface Props {
  customerId: number;
}

const GST_RATE = 0.09;
const STRIPE_FEE_RATE = 0.035;

function formatSGD(amount: number) {
  return `SGD ${amount.toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(d: Date | string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-SG", { day: "2-digit", month: "short", year: "numeric" });
}

function quoteType(quoteRef: string): "P" | "R" | "unknown" {
  if (/CLARI-P-/i.test(quoteRef)) return "P";
  if (/CLARI-R-/i.test(quoteRef)) return "R";
  return "unknown";
}

interface InstalmentRow {
  enabled: boolean;
  percentage: number;
  stage: string;
  scheduledDate?: string;  // optional YYYY-MM date for non-confirmation rows
}

const DEFAULT_INSTALMENTS: InstalmentRow[] = [
  { enabled: true, percentage: 50, stage: defaultPptPaymentTrancheLabel(0), scheduledDate: undefined },
  { enabled: true, percentage: 50, stage: defaultPptPaymentTrancheLabel(1), scheduledDate: undefined },
  { enabled: false, percentage: 0, stage: defaultPptPaymentTrancheLabel(2), scheduledDate: undefined },
  { enabled: false, percentage: 0, stage: "", scheduledDate: undefined },
];

// Payment method is auto-derived: if stripeFee is checked → "card", otherwise → "paynow"

interface ModalState {
  open: boolean;
  dealId: number;
  quoteRef: string;
  baseAmount: number;
  isRegenerate: boolean;
}

export function PurchaseHistoryTab({ customerId }: Props) {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const userRole = normalizeUserRole(user?.role);
  const isFinance = userRole === "finance" || userRole === "admin";
  const canEditWarranty = userRole === "sales" || userRole === "admin";
  const canViewWarranty = canViewWarrantyRole(user?.role);

  const { data: deals = [], refetch } = trpc.salesDeals.listByCustomer.useQuery({ customerId });
  const dealInvoiceIds = Array.from(
    new Set(
      (deals as any[]).flatMap((deal) =>
        (deal.invoices ?? [])
          .map((invoice: { id?: number }) => invoice.id)
          .filter((invoiceId: number | undefined): invoiceId is number => !!invoiceId && invoiceId > 0),
      ),
    ),
  );
  const warrantyActions = trpc.warranties.getActionsForDealInvoices.useQuery(
    { invoiceIds: dealInvoiceIds },
    { enabled: canViewWarranty && dealInvoiceIds.length > 0 },
  );

  const [modal, setModal] = useState<ModalState | null>(null);
  const [gst, setGst] = useState(true);
  const [cardAdminFee, setCardAdminFee] = useState(false);
  const [cardPaymentLink, setCardPaymentLink] = useState(false);
  const [instalments, setInstalments] = useState<InstalmentRow[]>(DEFAULT_INSTALMENTS.map((r) => ({ ...r })));

  const raiseInvoice = trpc.salesDeals.raiseInvoice.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.created.length} invoice(s) raised successfully`);
      setModal(null);
      refetch();
    },
    onError: (err) => toast.error(err.message || "Failed to raise invoice"),
  });

  const regenerateInvoice = trpc.salesDeals.regenerateInvoice.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.created.length} invoice(s) regenerated successfully`);
      setModal(null);
      refetch();
    },
    onError: (err) => toast.error(err.message || "Failed to regenerate invoice"),
  });

  const [confirmMarkPaidId, setConfirmMarkPaidId] = useState<number | null>(null);

  const markPaid = trpc.salesDeals.markInvoicePaid.useMutation({
    onSuccess: (data) => {
      toast.success(data?.message ?? "Payment recorded.");
      if (data?.receipt?.warning) {
        toast.warning(data.receipt.warning, { duration: 8000 });
      }
      for (const w of data?.warnings ?? []) {
        if (w && w !== data?.receipt?.warning && w !== data?.message) {
          toast.warning(w, { duration: 8000 });
        }
      }
      setConfirmMarkPaidId(null);
      refetch();
    },
    onError: (err) => toast.error(err.message || "Failed to mark paid"),
  });

  const sendXeroPayment = trpc.financeInvoices.sendXeroPayment.useMutation({
    onSuccess: (data) => {
      toast.success(data.alreadySent ? "Already sent to Xero" : "Sent to Xero");
      refetch();
    },
    onError: (err) => toast.error(err.message || "Failed to send to Xero"),
  });

  const retryWebhook = trpc.financeInvoices.retryWebhook.useMutation({
    onSuccess: () => { toast.success("Webhook resent to SAPO"); refetch(); },
    onError: (err) => toast.error(err.message || "Failed to retry webhook"),
  });

  const undoPayment = trpc.financeInvoices.undoPayment.useMutation({
    onSuccess: () => { toast.success("Payment reverted and invoice.voided sent to SAPO"); refetch(); },
    onError: (err) => toast.error(err.message || "Failed to undo payment"),
  });

  const utils = trpc.useUtils();

  const [reminderInvoiceId, setReminderInvoiceId] = useState<number | null>(null);
  const createWarranty = trpc.warranties.createDraft.useMutation({
    onError: (err) => toast.error(err.message || "Could not create warranty draft"),
  });



  const openModal = (deal: { id: number; quoteRef: string; totalAmount: string | null }, isRegenerate = false) => {
    setGst(true);
    setCardAdminFee(false);
    setCardPaymentLink(false);
    setInstalments(DEFAULT_INSTALMENTS.map((r) => ({ ...r })));
    setModal({
      open: true,
      dealId: deal.id,
      quoteRef: deal.quoteRef,
      baseAmount: deal.totalAmount ? Number(deal.totalAmount) : 0,
      isRegenerate,
    });
  };

  const updateInstalment = (idx: number, field: keyof InstalmentRow, value: boolean | number | string) => {
    setInstalments((prev) => {
      const updated = prev.map((r, i) => {
        if (i !== idx) return r;
        const next = { ...r, [field]: value } as InstalmentRow;
        if (field === "enabled" && value === true && shouldAutoFillPptPaymentTrancheLabel(r.stage)) {
          next.stage = resolvePptPaymentTrancheLabelForAdd(idx, r.stage);
        }
        return next;
      });
      // Auto-set to 100% when only one row is enabled
      const activeCount = updated.filter((r) => r.enabled).length;
      if (activeCount === 1) {
        return updated.map((r) =>
          r.enabled
            ? {
                ...r,
                percentage: 100,
                stage: shouldAutoFillPptPaymentTrancheLabel(r.stage)
                  ? defaultPptPaymentTrancheLabel(0)
                  : r.stage,
              }
            : r
        );
      }
      return updated;
    });
  };

  const activeInstalments = instalments.filter((r) => r.enabled && r.percentage > 0);
  const totalPct = activeInstalments.reduce((s, r) => s + r.percentage, 0);
  const pctValid = Math.abs(totalPct - 100) < 0.01;
  // All non-first instalments must have a scheduledDate (expected completion month)
  const missingDates = activeInstalments.filter((r, idx) => idx > 0 && !r.scheduledDate);
  const datesValid = missingDates.length === 0;
  const paymentMethod: "paynow" | "card" | "bank_transfer" | "cash" = "paynow";

  function previewAmount(pct: number): number {
    if (!modal) return 0;
    const base = Math.round(modal.baseAmount * (pct / 100) * 100) / 100;
    const afterGst = gst ? Math.round(base * (1 + GST_RATE) * 100) / 100 : base;
    const withCard =
      cardAdminFee && cardPaymentLink
        ? Math.round(afterGst * (1 + STRIPE_FEE_RATE) * 100) / 100
        : afterGst;
    return withCard;
  }

  function handleSubmit() {
    if (!modal || !pctValid || activeInstalments.length === 0 || !datesValid) return;
    const rows = activeInstalments.map((r) => ({ stage: r.stage, percentage: r.percentage, ...(r.scheduledDate ? { scheduledDate: r.scheduledDate } : {}) }));
    const payload = {
      dealId: modal.dealId,
      baseAmount: modal.baseAmount,
      gstApplied: gst,
      invoiceGstEnabled: gst,
      cardAdminFeeEnabled: cardAdminFee,
      cardPaymentLinkEnabled: cardPaymentLink,
      paymentMethod,
      instalments: rows,
    };
    if (modal.isRegenerate) {
      regenerateInvoice.mutate(payload);
    } else {
      raiseInvoice.mutate(payload);
    }
  }

  async function copyInvoiceLink(token: string) {
    const url = `${window.location.origin}/deal-invoice/${token}`;
    const copied = await copyTextToClipboard(url);
    if (copied) {
      toast.success("Invoice link copied.");
    } else {
      toast.error("Could not copy invoice link.");
    }
  }

  if (deals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Package className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-sm font-medium text-muted-foreground">No proposals received yet</p>
        <p className="text-xs text-muted-foreground mt-1">Approved deals from the Sales Portal will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {(deals as any[]).map((deal) => {
        const type = quoteType(deal.quoteRef);
        const invoices: any[] = deal.invoices ?? [];
        const hasInvoices = invoices.length > 0;

        return (
          <Card key={deal.id} className="border border-border">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <span className={`mt-0.5 px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide shrink-0 ${
                    type === "P" ? "bg-blue-100 text-blue-800" :
                    type === "R" ? "bg-purple-100 text-purple-800" :
                    "bg-gray-100 text-gray-700"
                  }`}>
                    {type === "P" ? "Project" : type === "R" ? "Retail" : "Deal"}
                  </span>
                  <div>
                    <CardTitle className="text-sm font-mono font-semibold">{deal.quoteRef}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {deal.salespersonName ?? "—"} · {formatDate(deal.occurredAt)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge variant={deal.event === "deal.completed" ? "default" : "secondary"} className="text-xs">
                    {deal.event === "deal.completed"
                      ? <><CheckCircle className="h-3 w-3 mr-1" />Completed</>
                      : <><Clock className="h-3 w-3 mr-1" />Projected</>}
                  </Badge>
                  {deal.totalAmount && (
                    <span className="text-sm font-semibold">{formatSGD(Number(deal.totalAmount))}</span>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-0 space-y-3">
              {hasInvoices && (
                <div className="space-y-2">
                  {invoices.map((inv: any) => {
                    if (String(inv.invoiceStatus ?? "").toLowerCase() === "voided") return null;
                    const isPaid = inv.invoiceStatus === "paid";
                    const invoicePdfUrl = `/api/deal-invoices/${inv.invoiceToken}/invoice.pdf`;
                    return (
                      <div key={inv.id} className="rounded-lg border border-border bg-muted/30 p-3">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <p className="text-xs font-semibold font-mono">{inv.invoiceNumber}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {inv.instalmentStage} · {inv.instalmentPercentage}% · {formatSGD(Number(inv.finalAmount))}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {inv.gstApplied ? "GST incl." : "No GST"}
                              {inv.stripeFeeApplied ? " · Card fee incl." : ""}
                              {(inv as { paymentLink?: string | null }).paymentLink ? " · HitPay" : ""}
                            </p>
                          </div>
                          <Badge variant={isPaid ? "default" : "outline"} className="text-xs shrink-0">
                            {isPaid
                              ? <><CheckCircle className="h-3 w-3 mr-1" />Paid</>
                              : <><Clock className="h-3 w-3 mr-1" />Raised</>}
                          </Badge>
                        </div>
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.open(invoicePdfUrl, "_blank")}>
                            <ExternalLink className="h-3 w-3 mr-1" />View Invoice
                          </Button>
                          {isPaid ? <ResendReceiptButton userRole={user?.role} invoiceId={`deal-${inv.id}`} /> : null}
                          {!isPaid ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => void copyInvoiceLink(inv.invoiceToken)}
                            >
                              <Copy className="h-3 w-3 mr-1" />Copy Invoice Link
                            </Button>
                          ) : inv.invoiceToken ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={async () => {
                                const url = `${window.location.origin}/deal-invoice/${inv.invoiceToken}`;
                                window.open(url, "_blank", "noopener,noreferrer");
                                const copied = await copyTextToClipboard(url);
                                if (copied) {
                                  toast.success("Receipt link copied.");
                                } else {
                                  toast.error("Could not copy receipt link.");
                                }
                              }}
                            >
                              <Copy className="h-3 w-3 mr-1" />View Receipt
                            </Button>
                          ) : null}
                          {(() => {
                            const action = warrantyActions.data?.[inv.id]?.action;
                            if (!action || !canViewWarranty) return null;
                            if (action.kind === "view") {
                              const status = warrantyActions.data?.[inv.id]?.card?.status;
                              const label =
                                status === "pending_approval"
                                  ? "REVIEW WARRANTY"
                                  : "VIEW WARRANTY ITEMS";
                              return (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => setLocation(`/warranties/${action.warrantyCardId}`)}
                                >
                                  <Shield className="h-3 w-3 mr-1" />{label}
                                </Button>
                              );
                            }
                            if (!canEditWarranty) return null;
                            if (action.kind === "continue") {
                              return (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => setLocation(`/warranties/${action.warrantyCardId}`)}
                                >
                                  <Shield className="h-3 w-3 mr-1" />CONTINUE WARRANTY DRAFT
                                </Button>
                              );
                            }
                            if (action.kind === "disabled") {
                              return (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  disabled
                                  title={action.message}
                                >
                                  <Shield className="h-3 w-3 mr-1" />CREATE WARRANTY DRAFT
                                </Button>
                              );
                            }
                            return (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                disabled={createWarranty.isPending}
                                onClick={() => {
                                  createWarranty.mutate(
                                    {
                                      sourceInvoiceType: "deal",
                                      sourceInvoiceId: inv.id,
                                    },
                                    {
                                      onSuccess: (result) => {
                                        toast.success("Warranty draft created");
                                        setLocation(`/warranties/${result.card.id}`);
                                      },
                                    },
                                  );
                                }}
                              >
                                <Shield className="h-3 w-3 mr-1" />CREATE WARRANTY DRAFT
                              </Button>
                            );
                          })()}
                          {isFinance &&
                            !isPaid &&
                            shouldShowMarkAsPaidButton({ role: user?.role, invoicePaid: false }) && (
                            <>
                              <Button
                                size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => setReminderInvoiceId(inv.id)}
                              >
                                <Mail className="h-3 w-3 mr-1" />Send reminder
                              </Button>
                              <Button
                                size="sm" variant="default" className="h-7 text-xs"
                                disabled={markPaid.isPending}
                                onClick={() => setConfirmMarkPaidId(inv.id)}
                              >
                                Mark as Paid
                              </Button>
                            </>
                          )}
                          {isFinance && isPaid && (
                            <>
                              <Button
                                size="sm" variant="outline" className="h-7 text-xs border-blue-500 text-blue-600 hover:bg-blue-50"
                                disabled={retryWebhook.isPending}
                                onClick={() => retryWebhook.mutate({ id: `deal-${inv.id}` })}
                                title="Resend webhook to SAPO"
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />Send to SAPO
                              </Button>
                              {shouldShowSentToXeroStatus({
                                invoicePaid: true,
                                xeroPaymentSyncStatus: (inv as { xeroPaymentSyncStatus?: string | null })
                                  .xeroPaymentSyncStatus,
                              }) ? (
                                <XeroPaymentSentStatus
                                  compact
                                  syncedAt={(inv as { xeroPaymentSyncedAt?: Date | string | null }).xeroPaymentSyncedAt}
                                  sentBy={(inv as { xeroPaymentSentBy?: string | null }).xeroPaymentSentBy}
                                />
                              ) : shouldShowSendToXeroButton({
                                  role: user?.role,
                                  invoicePaid: true,
                                  xeroPaymentSyncStatus: (inv as { xeroPaymentSyncStatus?: string | null })
                                    .xeroPaymentSyncStatus,
                                }) ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs border-sky-500 text-sky-700 hover:bg-sky-50"
                                  disabled={sendXeroPayment.isPending}
                                  onClick={() => sendXeroPayment.mutate({ unifiedId: `deal-${inv.id}` })}
                                >
                                  <RefreshCw
                                    className={`h-3 w-3 mr-1 ${sendXeroPayment.isPending ? "animate-spin" : ""}`}
                                  />
                                  {sendXeroPayment.isPending ? "Sending…" : "Send to Xero"}
                                </Button>
                              ) : null}
                              <Button
                                size="sm" variant="outline" className="h-7 text-xs border-red-500 text-red-600 hover:bg-red-50"
                                disabled={undoPayment.isPending}
                                onClick={() => { if (confirm("Undo Mark as Paid and send invoice.voided to SAPO?")) undoPayment.mutate({ id: `deal-${inv.id}` }); }}
                                title="Revert payment and send void webhook"
                              >
                                <X className="h-3 w-3 mr-1" />Undo Mark as Paid
                              </Button>
                            </>
                          )}
                        </div>
                        {/* Send confirmation lines */}
                        {inv.whatsappSentAt && inv.whatsappSentBy && (
                          <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3 shrink-0" />
                            Successfully sent to customer via WhatsApp on{" "}
                            {new Date(inv.whatsappSentAt).toLocaleDateString("en-SG", { day: "numeric", month: "long", year: "numeric" })}{" "}
                            {new Date(inv.whatsappSentAt).toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit", hour12: true })}{" "}
                            by {inv.whatsappSentBy}
                          </p>
                        )}
                        {inv.emailSentAt && inv.emailSentBy && (
                          <p className="text-xs text-blue-700 mt-1 flex items-center gap-1">
                            <CheckCircle className="h-3 w-3 shrink-0" />
                            Successfully sent to customer via Email on{" "}
                            {new Date(inv.emailSentAt).toLocaleDateString("en-SG", { day: "numeric", month: "long", year: "numeric" })}{" "}
                            {new Date(inv.emailSentAt).toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit", hour12: true })}{" "}
                            by {inv.emailSentBy}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {isFinance && (
                <div className="flex gap-2 pt-1">
                  {!hasInvoices ? (
                    <Button size="sm" variant="outline" onClick={() => openModal(deal, false)}>
                      <FileText className="h-3.5 w-3.5 mr-1.5" />Raise Invoice
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" className="text-xs text-muted-foreground" onClick={() => openModal(deal, true)}>
                      <RefreshCw className="h-3 w-3 mr-1" />Regenerate Invoice
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Invoice Modal */}
      {modal && (
        <Dialog open={modal.open} onOpenChange={(o) => !o && setModal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{modal.isRegenerate ? "Regenerate" : "Raise"} Invoice — {modal.quoteRef}</DialogTitle>
            </DialogHeader>

            <div className="space-y-5 py-2">
              <div className="bg-muted/40 rounded-lg p-3 text-sm">
                <span className="text-muted-foreground">Proposal total: </span>
                <span className="font-semibold">{formatSGD(modal.baseAmount)}</span>
              </div>

              {/* Instalment rows */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Payment Terms</Label>
                <div className="space-y-2">
                  {instalments.map((row, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={row.enabled}
                          onChange={(e) => updateInstalment(idx, "enabled", e.target.checked)}
                          className="rounded shrink-0 h-4 w-4"
                        />
                        <Input
                          type="number" min={1} max={100}
                          value={row.enabled ? row.percentage : ""}
                          onChange={(e) => updateInstalment(idx, "percentage", Number(e.target.value))}
                          disabled={!row.enabled}
                          className="w-16 h-8 text-sm text-center"
                          placeholder="%"
                        />
                        <span className="text-xs text-muted-foreground shrink-0">%</span>
                        <Input
                          value={row.stage}
                          onChange={(e) => updateInstalment(idx, "stage", e.target.value)}
                          disabled={!row.enabled}
                          className="flex-1 h-8 text-sm"
                          placeholder="Stage label"
                        />
                        {row.enabled && row.percentage > 0 && (
                          <span className="text-xs font-medium shrink-0 w-28 text-right tabular-nums">
                            {formatSGD(previewAmount(row.percentage))}
                          </span>
                        )}
                      </div>
                      {/* Date picker for non-first rows (not Confirmation) */}
                      {idx > 0 && row.enabled && (
                        <div className="pl-6 flex items-center gap-2">
                          <span className="text-xs text-muted-foreground w-28 shrink-0">Expected date</span>
                          <Input
                            type="month"
                            value={row.scheduledDate ?? ""}
                            onChange={(e) => updateInstalment(idx, "scheduledDate", e.target.value)}
                            className="h-7 text-xs w-40"
                          />
                          {!row.scheduledDate && (
                            <span className="text-xs text-destructive font-medium flex items-center gap-1"><AlertCircle className="h-3 w-3" />Required — enter the expected completion month</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {!pctValid && activeInstalments.length > 0 && (
                  <p className="text-xs text-destructive mt-1.5 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Total is {totalPct}% — must equal exactly 100%
                  </p>
                )}
                {pctValid && activeInstalments.length > 0 && (
                  <p className="text-xs text-green-600 mt-1.5">✓ Total: 100%</p>
                )}
              </div>

              {/* Invoice payment settings (HitPay) */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Invoice payment settings</Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={gst} onChange={(e) => setGst(e.target.checked)} className="rounded h-4 w-4" />
                    <span className="text-sm">Apply 9% GST</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cardAdminFee}
                      onChange={(e) => setCardAdminFee(e.target.checked)}
                      className="rounded h-4 w-4"
                    />
                    <span className="text-sm">Add 3.5% credit card administrative fee</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cardPaymentLink}
                      onChange={(e) => setCardPaymentLink(e.target.checked)}
                      className="rounded h-4 w-4"
                    />
                    <span className="text-sm">Create credit card payment link (HitPay)</span>
                  </label>
                  {cardAdminFee && cardPaymentLink && (
                    <p className="text-xs text-amber-600 flex items-center gap-1 pl-6">
                      <AlertCircle className="h-3 w-3 flex-shrink-0" />
                      Card fee is included in the invoice total and HitPay checkout amount.
                    </p>
                  )}
                </div>
              </div>

              {/* Payment method is auto-derived — no dropdown needed */}

              {/* Summary */}
              {activeInstalments.length > 0 && pctValid && (
                <div className="bg-muted/40 rounded-lg p-3 space-y-1.5 text-sm">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Invoice Summary</p>
                  {activeInstalments.map((row, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-muted-foreground">{row.stage} ({row.percentage}%)</span>
                      <span className="font-medium tabular-nums">{formatSGD(previewAmount(row.percentage))}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-semibold pt-1.5 border-t mt-1">
                    <span>Total invoiced</span>
                    <span className="tabular-nums">{formatSGD(activeInstalments.reduce((s, r) => s + previewAmount(r.percentage), 0))}</span>
                  </div>
                  <p className="text-xs text-muted-foreground pt-1">
                    {activeInstalments.length} invoice(s) will be generated.
                    {cardPaymentLink ? " HitPay card links created when enabled." : " No card link — customers see contact-salesperson message."}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
              <Button
                disabled={!pctValid || !datesValid || activeInstalments.length === 0 || raiseInvoice.isPending || regenerateInvoice.isPending}
                onClick={handleSubmit}
              >
                {raiseInvoice.isPending || regenerateInvoice.isPending
                  ? "Processing..."
                  : modal.isRegenerate ? "Regenerate Invoice" : "Raise Invoice"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <MarkPaidReceiptModeDialog
        open={confirmMarkPaidId != null}
        onOpenChange={(open) => {
          if (!open) setConfirmMarkPaidId(null);
        }}
        pending={markPaid.isPending}
        onSubmit={(payload) => {
          if (confirmMarkPaidId == null) return;
          markPaid.mutate({ invoiceId: confirmMarkPaidId, ...payload });
        }}
      />
      <SendInvoiceReminderDialog
        open={reminderInvoiceId != null}
        invoiceId={reminderInvoiceId}
        onOpenChange={(o) => {
          if (!o) setReminderInvoiceId(null);
        }}
        onSettled={() => {
          utils.salesDeals.listByCustomer.invalidate({ customerId });
          refetch();
        }}
      />
    </div>
  );
}
